import { toast } from "react-toastify";
import { SearchOutlined } from "@ant-design/icons";
import { Box } from "@mui/material";
import { Form, Space, Switch, Table, Typography } from "antd";
import type { ColumnsType } from "antd/lib/table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../axios/axios-instance";
import ActionButton from "../../../components/common/buttons/ActionButton";
import InputField from "../../../components/common/fields/InputField";
import NumberField from "../../../components/common/fields/NumberField";
import SearchInput from "../../../components/common/fields/SearchInput";
import SettingsPanel, { SwitchSetting } from "../../../components/common/SettingsPanel";
import Card from "../../../components/common/Card";
import { pathAPI } from "../../../utils/pathApi";

interface LoyaltySettings {
  enabled: boolean;
  default_earning_enabled: boolean;
  default_points_per_service: number;
  earn_on_order_status: string;
  allow_guest_earn: boolean;
  reverse_on_refund: boolean;
}

interface ServiceLoyaltyConfig {
  config_id?: number | null;
  service_id: number;
  service_name: string;
  outlet_id?: number | null;
  outlet_name?: string | null;
  category_id?: number | null;
  category_name?: string | null;
  price?: number | null;
  discount_price?: number | null;
  earning_enabled?: boolean | null;
  points_per_unit?: number | null;
  last_modified_date?: string | null;
}

const NUMBER_FIELD_WIDTH = 140;
const SEARCH_INPUT_WIDTH = 280;
const ROW_BUTTON_MIN_WIDTH = 90;

const unwrap = (response: any) => response?.data?.data ?? response?.data;

const pageItems = (payload: any): ServiceLoyaltyConfig[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const pageTotal = (payload: any, fallback: number) =>
  payload?.total_elements ?? payload?.totalElements ?? payload?.total ?? fallback;

const defaultSettings: LoyaltySettings = {
  enabled: false,
  default_earning_enabled: false,
  default_points_per_service: 0,
  earn_on_order_status: "ORDER",
  allow_guest_earn: false,
  reverse_on_refund: true,
};

const LoyaltyPoints = () => {
  const { t } = useTranslation();
  const switchSettings: SwitchSetting[] = [
    { name: "enabled", label: t("loyalty.switch.enabled"), description: t("loyalty.switch.enabled_desc") },
    { name: "default_earning_enabled", label: t("loyalty.switch.default_earning"), description: t("loyalty.switch.default_earning_desc") },
    { name: "allow_guest_earn", label: t("loyalty.switch.guest_earn"), description: t("loyalty.switch.guest_earn_desc") },
    { name: "reverse_on_refund", label: t("loyalty.switch.reverse_refund"), description: t("loyalty.switch.reverse_refund_desc") },
  ];
  const [settingsForm] = Form.useForm<LoyaltySettings>();
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [savingServiceId, setSavingServiceId] = useState<number | null>(null);
  const [services, setServices] = useState<ServiceLoyaltyConfig[]>([]);
  const [drafts, setDrafts] = useState<Record<number, { earning_enabled: boolean; points_per_unit: number }>>({});
  const [keyword, setKeyword] = useState("");
  const [outletId, setOutletId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const fetchSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const response = await axiosInstance.get(`${pathAPI.loyalty}/settings`);
      settingsForm.setFieldsValue({ ...defaultSettings, ...unwrap(response) });
    } catch (error) {
      toast.error(t("loyalty.settings_load_error"));
    } finally {
      setSettingsLoading(false);
    }
  }, [settingsForm, t]);

  const fetchServices = useCallback(
    async (nextPage: number, nextPageSize: number) => {
      setServicesLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(nextPage - 1),
          size: String(nextPageSize),
        });
        if (keyword.trim()) params.set("keyword", keyword.trim());
        if (outletId) params.set("outlet_id", String(outletId));

        const response = await axiosInstance.get(`${pathAPI.loyalty}/service-configs?${params}`);
        const payload = unwrap(response);
        const items = pageItems(payload);
        setServices(items);
        setTotal(pageTotal(payload, items.length));
        setDrafts(
          items.reduce((acc, item) => {
            acc[item.service_id] = {
              earning_enabled: item.earning_enabled ?? true,
              points_per_unit: item.points_per_unit ?? 0,
            };
            return acc;
          }, {} as Record<number, { earning_enabled: boolean; points_per_unit: number }>)
        );
      } catch (error) {
        toast.error(t("loyalty.services_load_error"));
      } finally {
        setServicesLoading(false);
      }
    },
    [keyword, outletId, t]
  );

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    fetchServices(1, pageSize);
  }, [fetchServices, pageSize]);

  const saveSettings = async (values: LoyaltySettings) => {
    setSettingsLoading(true);
    try {
      const response = await axiosInstance.put(`${pathAPI.loyalty}/settings`, values);
      settingsForm.setFieldsValue({ ...defaultSettings, ...unwrap(response) });
      toast.success(t("loyalty.settings_saved"));
    } catch (error) {
      toast.error(t("loyalty.settings_save_error"));
    } finally {
      setSettingsLoading(false);
    }
  };

  const updateDraft = (
    serviceId: number,
    patch: Partial<{ earning_enabled: boolean; points_per_unit: number }>
  ) => {
    setDrafts((prev) => ({
      ...prev,
      [serviceId]: {
        earning_enabled: prev[serviceId]?.earning_enabled ?? true,
        points_per_unit: prev[serviceId]?.points_per_unit ?? 0,
        ...patch,
      },
    }));
  };

  const saveServiceConfig = async (serviceId: number) => {
    const draft = drafts[serviceId] || { earning_enabled: true, points_per_unit: 0 };
    setSavingServiceId(serviceId);
    try {
      await axiosInstance.put(`${pathAPI.loyalty}/services/${serviceId}/config`, draft);
      toast.success(t("loyalty.service_saved"));
      fetchServices(page, pageSize);
    } catch (error) {
      toast.error(t("loyalty.service_save_error"));
    } finally {
      setSavingServiceId(null);
    }
  };

  const columns: ColumnsType<ServiceLoyaltyConfig> = useMemo(
    () => [
      {
        title: t("loyalty.col.service"),
        dataIndex: "service_name",
        key: "service_name",
        render: (_, record) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{record.service_name}</Typography.Text>
            <Typography.Text type="secondary">{record.category_name || t("loyalty.col.no_category")}</Typography.Text>
          </Space>
        ),
      },
      {
        title: t("loyalty.col.outlet"),
        dataIndex: "outlet_name",
        key: "outlet_name",
        render: (value) => value || "-",
      },
      {
        title: t("loyalty.col.price"),
        key: "price",
        width: 120,
        render: (_, record) => record.discount_price ?? record.price ?? 0,
      },
      {
        title: t("loyalty.col.earning"),
        key: "earning_enabled",
        width: 120,
        render: (_, record) => (
          <Switch
            checked={drafts[record.service_id]?.earning_enabled ?? true}
            onChange={(checked) => updateDraft(record.service_id, { earning_enabled: checked })}
          />
        ),
      },
      {
        title: t("loyalty.col.points"),
        key: "points_per_unit",
        width: 140,
        render: (_, record) => (
          <NumberField
            min={0}
            precision={0}
            width={NUMBER_FIELD_WIDTH}
            value={drafts[record.service_id]?.points_per_unit ?? 0}
            onChange={(value) => updateDraft(record.service_id, { points_per_unit: Number(value || 0) })}
          />
        ),
      },
      {
        title: t("loyalty.col.action"),
        key: "action",
        width: 120,
        render: (_, record) => (
          <ActionButton
            sx={{ minWidth: ROW_BUTTON_MIN_WIDTH }}
            loading={savingServiceId === record.service_id}
            onClick={() => saveServiceConfig(record.service_id)}
          >
            {t("loyalty.col.save")}
          </ActionButton>
        ),
      },
    ],
    [drafts, savingServiceId, t]
  );

  return (
    <Card className="user">
      <Space direction="vertical" size={24} style={{ width: "100%" }}>
        <Form
          form={settingsForm}
          layout="vertical"
          initialValues={defaultSettings}
          onFinish={saveSettings}
          disabled={settingsLoading}
        >
          <SettingsPanel
            title={t("loyalty.settings_title")}
            loading={settingsLoading}
            onReload={fetchSettings}
            switches={switchSettings}
          >
            <Form.Item name="default_points_per_service" label={t("loyalty.default_points")}>
              <NumberField min={0} precision={0} />
            </Form.Item>
            <Form.Item name="earn_on_order_status" label={t("loyalty.earn_status")}>
              <InputField />
            </Form.Item>
          </SettingsPanel>
        </Form>

        <Space wrap>
          <Box width={SEARCH_INPUT_WIDTH}>
            <SearchInput
              placeholder={t("loyalty.search_ph")}
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              onSearch={() => {
                setPage(1);
                fetchServices(1, pageSize);
              }}
            />
          </Box>
          <NumberField
            placeholder={t("loyalty.outlet_id_ph")}
            min={1}
            width={NUMBER_FIELD_WIDTH}
            value={outletId}
            onChange={(value) => setOutletId(value ? Number(value) : null)}
          />
          <ActionButton
            appSize="large"
            startIcon={<SearchOutlined />}
            onClick={() => {
              setPage(1);
              fetchServices(1, pageSize);
            }}
            loading={servicesLoading}
          >
            {t("loyalty.search")}
          </ActionButton>
        </Space>

        <Table
          rowKey="service_id"
          columns={columns}
          dataSource={services}
          loading={servicesLoading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (total, range) => t("loyalty.range", { from: range[0], to: range[1], total }),
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
              fetchServices(nextPage, nextPageSize);
            },
          }}
        />
      </Space>
    </Card>
  );
};

export default LoyaltyPoints;
